/**
 * NOTE: This component duplicates the component at /components/products/ProductCard/ProductCard.tsx
 *
 * The other version should be used when these two components are unified.
 */

import React, { AnchorHTMLAttributes } from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";

import { BrandCatalogProduct } from "api/requests/getProductCatalog";
import { BrandFeaturedProducts } from "custom-types/Brand";
import { Product } from "custom-types/Product";
import { getCountryCode } from "redux/selectors/location";
import currencyFormatter from "utils/currencyFormatter";

import StarRating from "components/botanic/StarRating";
import Image from "components/Image";

type Props = {
  product: BrandCatalogProduct | BrandFeaturedProducts | Product;
  className?: string;
  cardHref?: string;
} & AnchorHTMLAttributes<HTMLAnchorElement>;

const ProductCard = ({ product, className, cardHref, ...props }: Props) => {
  const countryCode = useSelector(getCountryCode);

  const isBrandFeaturedProduct = "customize" in product;

  const thumbnailUrl =
    !isBrandFeaturedProduct && product.thumbnailImageUrl
      ? product.thumbnailImageUrl
      : product.imageUrl;

  return (
    <a
      className={classNames(
        "elevation-low rounded overflow-hidden p-lg pt-xl flex flex-col justify-between",
        className
      )}
      href={
        isBrandFeaturedProduct
          ? cardHref
          : `/brands/${product.brand.slug}/products/${product.slug}`
      }
      {...props}
    >
      <div>
        <div className="p-0 mx-auto" style={{ width: "112px" }}>
          {thumbnailUrl && (
            <Image
              src={thumbnailUrl}
              sizes={[112]}
              className="mb-md"
              alt={`Product image for ${product.name}`}
              ratio="square"
            />
          )}
        </div>
        <div className="text-secondary text-xs leading-none mb-xs">
          {product.subcategoryName || isBrandFeaturedProduct
            ? product.subcategoryName
            : product.categoryName}
        </div>
        <h3 className="font-bold text-sm truncate-lines truncate-2-lines">
          {product.name}
        </h3>
        {!!product.avgApprovedPublicReviewRating && (
          <StarRating
            rating={product.avgApprovedPublicReviewRating}
            ratingCount={product.countApprovedPublicReviews}
            showRating={true}
            starWidth={14}
            className="my-xs"
          />
        )}
      </div>
      {!isBrandFeaturedProduct && !!product.bestOfferVariant && (
        <div className="mt-xs text-xs font-bold text-grey">
          Starting at
          <div className="text-sm font-bold text-default">
            {currencyFormatter(product.bestOfferVariant.price, countryCode)}
          </div>
        </div>
      )}
    </a>
  );
};

export default ProductCard;
